import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Search, Check, X, Star, BarChart, Calendar, AlertTriangle, 
    PlusCircle, ChevronDown, ChevronUp, Users, BookOpen, Heart, 
    Trash2, Trophy, Clock, Share2, Edit 
} from 'lucide-react';
import { UserGame } from '../../../lib/game/collectionService';
import { GameSessionStats } from '../../../lib/game/sessionService';
import { usePlayedGamesCollection } from '../hooks/usePlayedGamesCollection'; // Importa o hook

interface GamesCollectionViewProps {
  // Ações que precisam interagir com outros hooks/componentes (modal, sessões)
  onAddSession: (game: UserGame) => void;
  onAddSessionGeneral?: () => void;
  // Hook de Coleção é gerenciado aqui dentro
  collectionHook: ReturnType<typeof usePlayedGamesCollection>; 
  gameSessionStats: { [gameId: string]: GameSessionStats };
}

const GamesCollectionView: React.FC<GamesCollectionViewProps> = ({ 
  onAddSession, 
  onAddSessionGeneral,
  collectionHook,
  gameSessionStats // Recebe as estatísticas como prop
}) => {
  const navigate = useNavigate();
  const { 
    pagedPlayedGames,
    totalCount,
    loading,
    error,
    searchText,
    sortConfig,
    currentPage,
    totalPages,
    handleSearchChange,
    handleSortChange,
    handlePageChange,
    handleRateGame,
    // handleTogglePlayed, // Usado internamente ou removido se não houver botão
    handleRemoveGame,
    handleUpdatePlayCount,
    handleToggleOwned,
    handleToggleWishlist,
  } = collectionHook;

  // Criar uma cópia da lista de jogos para reordenar localmente se necessário
  let displayedGames = [...pagedPlayedGames];

  if (sortConfig.sortBy === 'ug_play_count' && Object.keys(gameSessionStats).length > 0) {
    displayedGames.sort((a, b) => {
      const playsA = gameSessionStats[a.game_id]?.total_plays ?? a.play_count ?? 0;
      const playsB = gameSessionStats[b.game_id]?.total_plays ?? b.play_count ?? 0;
      
      if (sortConfig.ascending) {
        return playsA - playsB;
      }
      return playsB - playsA;
    });
  }

  const sortOptions = [
    { value: 'game_name', label: 'Nome' },
    { value: 'ug_rating', label: 'Sua Nota' },
    { value: 'g_average_rating', label: 'Nota Média (Jogo)' },
    { value: 'g_complexity', label: 'Complexidade (Jogo)' },
    { value: 'g_year', label: 'Ano Lançamento' },
    { value: 'ug_updated_at', label: 'Última Atualização' },
    { value: 'ug_play_count', label: 'Mais Jogados' },
  ];

  const handleConfirmRemoveGame = (gameId: string) => {
    if (window.confirm("Você quer remover o jogo totalmente da sua coleção ou apenas zerar o contador de partidas?\n\nClique em 'OK' para apenas ZERAR O CONTADOR e apagar o histórico de partidas.\nClique em 'Cancelar' para ver mais opções.")) {
      // Usuário escolheu apenas zerar o contador
      collectionHook.handleRemovePlayedGameHistory(gameId);
    } else {
      // Usuário escolheu "Cancelar", perguntar se quer remover completamente
      if (window.confirm("Deseja REMOVER COMPLETAMENTE este jogo da sua coleção? Isso o removerá de todas as listas (jogados, possuídos, desejados).")) {
        collectionHook.handleRemoveGame(gameId);
      }
    }
  };

  return (
    <>
      {/* Filtros e Ordenação */}
      <div className="flex flex-col sm:flex-row justify-between items-start mb-6 gap-3">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)} // Atualizado para passar o valor
              placeholder="Buscar jogos jogados"
              className="block w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 
                      focus:outline-none focus:ring-indigo-500 dark:focus:ring-indigo-500 focus:border-indigo-500 dark:focus:border-indigo-500"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={sortConfig.sortBy}
              onChange={(e) => handleSortChange(e.target.value)} // Atualizado
              className="text-sm min-w-[140px] pl-3 pr-5 py-2 border border-gray-300 dark:border-gray-600 
                      focus:outline-none focus:ring-indigo-500 dark:focus:ring-indigo-500 focus:border-indigo-500 dark:focus:border-indigo-500 rounded-md bg-white dark:bg-gray-700 dark:text-gray-100"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              onClick={() => handleSortChange(sortConfig.sortBy)} // Clica para inverter a direção atual
              className="flex items-center justify-center min-w-[90px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              {sortConfig.ascending ? (
                <><ChevronUp className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Crescente</span></>
              ) : (
                <><ChevronDown className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Decrescente</span></>
              )}
            </button>
          </div>
        </div>
        
        {onAddSessionGeneral && (
          <div className="w-full sm:w-auto mt-2 sm:mt-0">
              <button
                  onClick={onAddSessionGeneral}
                  className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white px-4 py-2 rounded text-sm flex items-center justify-center"
              >
                  <PlusCircle className="h-4 w-4 mr-1" />
                  Registrar Partida
              </button>
          </div>
        )}
      </div>

      {/* Conteúdo da Coleção */}
      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 dark:border-indigo-400 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Carregando seus jogos jogados...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/70 p-4 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400 dark:text-red-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Erro ao carregar seus jogos
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      ) : pagedPlayedGames.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">Nenhum jogo jogado encontrado</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchText 
                ? `Nenhum jogo encontrado para "${searchText}".` 
                : 'Você ainda não marcou nenhum jogo como jogado ou eles não foram carregados.'}
            </p>
          <button onClick={() => navigate('/search')} className="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white font-bold py-2 px-6 rounded-lg inline-flex items-center transition duration-150 ease-in-out">
            <PlusCircle className="h-5 w-5 mr-2" /> Adicionar Jogos
          </button>
        </div>
      ) : (
        <>
          {/* Tabela Desktop */}
          <div className="hidden md:block bg-white dark:bg-gray-800 shadow dark:shadow-slate-700 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="dark:bg-gray-750">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Jogo
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Jogadas
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Sua Nota
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                    Estatísticas
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {displayedGames.map(userGame => (
                  <tr key={userGame.game_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    {/* Coluna Jogo */}
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                             {/* Lógica da Imagem (mantida) */}
                            {userGame.game?.thumb_url ? (
                                <img
                                  className="h-10 w-10 rounded-sm object-cover"
                                  src={userGame.game.thumb_url.startsWith("http") ? userGame.game.thumb_url : (userGame.game.image_url || '/placeholder-game.png')}
                                  alt={userGame.game?.name || 'Imagem do jogo'}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).onerror = null;
                                    (e.target as HTMLImageElement).src = userGame.game?.image_url || '/placeholder-game.png';
                                  }}
                                />
                              ) : userGame.game?.image_url ? (
                                <img
                                  className="h-10 w-10 rounded-sm object-cover"
                                  src={userGame.game.image_url}
                                  alt={userGame.game?.name || 'Imagem do jogo'}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).onerror = null;
                                    (e.target as HTMLImageElement).src = '/placeholder-game.png';
                                  }}
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-sm bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-500">
                                  <BookOpen className="h-5 w-5" />
                                </div>
                              )}
                          </div>
                          <div className="ml-4">
                            <Link
                              to={`/games/${userGame.game?.slug || userGame.game_id}`}
                              className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400"
                            >
                              {userGame.game?.name || 'Jogo Desconhecido'}
                            </Link>
                            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                              {userGame.game?.year && <span>{userGame.game.year}</span>}
                              {userGame.owned && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200">Possui</span>}
                              {userGame.wishlist && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">Lista</span>}
                            </div>
                          </div>
                        </div>
                    </td>
                    {/* Coluna Jogadas */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                        {gameSessionStats[userGame.game_id]?.total_plays ?? userGame.play_count ?? 0}
                      </div>
                    </td>
                    {/* Coluna Sua Nota */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {/* Lógica de estrelas (mantida) */}
                        {userGame.rating ? (
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 mr-2">{userGame.rating}/5</span>
                              <div className="flex">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={star}
                                    onClick={() => handleRateGame(userGame.game_id, star)}
                                    className={`p-0.5 ${star <= (userGame.rating || 0) ? 'text-yellow-400 dark:text-yellow-500' : 'text-gray-300 dark:text-gray-600 hover:text-yellow-400 dark:hover:text-yellow-500'}`}
                                  >
                                    <Star className="h-4 w-4" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <button 
                              onClick={() => handleRateGame(userGame.game_id, 0)} // Ou algum valor default
                              className="text-xs text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400"
                            >
                              Avaliar
                            </button>
                          )}
                      </div>
                    </td>
                    {/* Coluna Estatísticas */}
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                      {/* Lógica de estatísticas (mantida e adaptada para dark) */}
                      {gameSessionStats[userGame.game_id] ? (
                          <>
                            <div>Vitórias: {gameSessionStats[userGame.game_id].total_wins || 0}</div>
                            <div>Derrotas: {gameSessionStats[userGame.game_id].total_losses || 0}</div>
                            <div>Empates: {gameSessionStats[userGame.game_id].total_draws || 0}</div>
                          </>
                      ) : (
                        <span className="italic">N/A</span>
                      )}
                    </td>
                    {/* Coluna Ações */}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => onAddSession(userGame)}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 p-1 rounded-md hover:bg-indigo-50 dark:hover:bg-gray-700"
                            title="Registrar Nova Partida"
                          >
                            <PlusCircle className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleConfirmRemoveGame(userGame.game_id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded-md hover:bg-red-50 dark:hover:bg-gray-700"
                            title="Remover Jogo/Partidas"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards Mobile */}
          <div className="md:hidden space-y-4">
            {displayedGames.map(userGame => (
              <div key={userGame.game_id} className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                <div className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {userGame.game?.thumb_url ? (
                          <img className="h-16 w-16 rounded-md object-cover" src={userGame.game.thumb_url.startsWith("http") ? userGame.game.thumb_url : (userGame.game.image_url || '/placeholder-game.png')} alt={userGame.game?.name} 
                            onError={(e) => { (e.target as HTMLImageElement).src = userGame.game?.image_url || '/placeholder-game.png'; }}
                          />
                      ) : userGame.game?.image_url ? (
                          <img className="h-16 w-16 rounded-md object-cover" src={userGame.game.image_url} alt={userGame.game?.name}
                            onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder-game.png'; }}
                          />
                      ) : (
                          <div className="h-16 w-16 rounded-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500">
                              <BookOpen className="h-8 w-8" />
                          </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/games/${userGame.game?.slug || userGame.game_id}`} className="text-base font-medium text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400 truncate block">
                        {userGame.game?.name || 'Jogo Desconhecido'}
                      </Link>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {userGame.game?.year && <span>{userGame.game.year}</span>}
                        {userGame.owned && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200">Possui</span>}
                        {userGame.wishlist && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">Lista</span>}
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                        <span className="font-semibold">
                          {gameSessionStats[userGame.game_id]?.total_plays ?? userGame.play_count ?? 0}
                        </span> jogada(s)
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Sua Nota:</span>
                      <div className="flex items-center">
                        {userGame.rating ? (
                          <>
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200 mr-1">{userGame.rating}/5</span>
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map(star => (
                                <button key={star} onClick={() => handleRateGame(userGame.game_id, star)} className={`p-0.5 ${star <= (userGame.rating || 0) ? 'text-yellow-400 dark:text-yellow-500' : 'text-gray-300 dark:text-gray-600 hover:text-yellow-400 dark:hover:text-yellow-500'}`}>
                                  <Star className="h-4 w-4" />
                                </button>
                              ))}
                            </div>
                          </>
                        ) : (
                          <button onClick={() => handleRateGame(userGame.game_id, 0)} className="text-xs text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400">
                            Avaliar
                          </button>
                        )}
                      </div>
                    </div>

                    {gameSessionStats[userGame.game_id] && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Vitórias: {gameSessionStats[userGame.game_id].total_wins || 0} | 
                        Derrotas: {gameSessionStats[userGame.game_id].total_losses || 0} | 
                        Empates: {gameSessionStats[userGame.game_id].total_draws || 0}
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/60 px-4 py-2 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex justify-end space-x-2">
                  <button
                      onClick={() => onAddSession(userGame)}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-gray-600 text-xs flex items-center"
                      title="Registrar Nova Partida"
                    >
                      <PlusCircle className="h-4 w-4 mr-1" /> Registrar
                    </button>
                    <button
                      onClick={() => handleConfirmRemoveGame(userGame.game_id)}
                      className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-gray-600 text-xs flex items-center"
                      title="Remover Jogo/Partidas"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Remover
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0 px-2">
              <p className="text-sm text-gray-700 dark:text-gray-400">
                Página <span className="font-medium">{currentPage}</span> de <span className="font-medium">{totalPages}</span>
              </p>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className={`relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium transition-colors
                    ${currentPage === 1 || loading 
                      ? 'bg-gray-100 dark:bg-gray-750 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                      : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}
                >
                  Anterior
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                  className={`relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium transition-colors
                  ${currentPage >= totalPages || loading 
                      ? 'bg-gray-100 dark:bg-gray-750 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                      : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}
                >
                  Próxima
                </button>
              </nav>
            </div>
          )}
          <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">Total de {totalCount} jogos jogados.</p>
        </>
      )}
    </>
  );
};

export default GamesCollectionView; 